import { VISUAL_FEATURE_KEYS, VisualFeatureKey } from '../constants/enums/VisualFeatureKey';
import type { IFeatures } from '../contexts/reducer/featureFlagsReducer';

export type VisualFeaturesValue = Partial<Record<VisualFeatureKey, boolean>>;

const VISUAL_FEATURE_KEY_SET = new Set<string>(VISUAL_FEATURE_KEYS);

let hasWarnedAboutLegacyArray = false;

const isVisualFeatureKey = (name: string): name is VisualFeatureKey => {
  if (VISUAL_FEATURE_KEY_SET.has(name)) {
    return true;
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`QSEM_VISUAL_FEATURES contains unknown flag "${name}"; it will be ignored.`);
  }

  return false;
};

export const normalizeVisualFeatures = (raw: unknown): VisualFeaturesValue => {
  if (Array.isArray(raw)) {
    if (!hasWarnedAboutLegacyArray && process.env.NODE_ENV !== 'production') {
      hasWarnedAboutLegacyArray = true;
      // eslint-disable-next-line no-console
      console.warn(
        'QSEM_VISUAL_FEATURES is still in the deprecated array format. Run the migration SQL script for this instance.',
      );
    }

    return raw.reduce((acc: VisualFeaturesValue, name) => {
      if (typeof name === 'string' && name && isVisualFeatureKey(name)) {
        return { ...acc, [name]: true };
      }

      return acc;
    }, {});
  }

  if (raw && typeof raw === 'object') {
    return Object.entries(raw).reduce((acc: VisualFeaturesValue, [key, value]) => {
      if (isVisualFeatureKey(key)) {
        return { ...acc, [key]: Boolean(value) };
      }

      return acc;
    }, {});
  }

  return {};
};

export const isVisualFeatureEnabled = (
  visualFeatures: VisualFeaturesValue | null | undefined,
  featureName: VisualFeatureKey,
): boolean => Boolean(visualFeatures?.[featureName]);

export const getVisualFeature = (features: IFeatures | undefined, key: VisualFeatureKey): boolean =>
  Boolean(features?.qsemVisualFeatures?.[key]);
